import React, { useEffect, useState, createContext, useContext } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Search, Upload, X, Plus, Trash2, Check, Circle, Dot, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { ProfilePhoto, IdDocument } from "@/components/PhotoCapture";
import { usePortalBase } from "@/context/PortalContext";
import { useI18n } from "@/context/I18nContext";

const IN = "w-full bg-[#1e1e1e] border border-white/25 rounded-md px-3 py-2 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand";
const PREF = ["北海道","青森県","岩手県","宮城県","秋田県","山形県","福島県","茨城県","栃木県","群馬県","埼玉県","千葉県","東京都","神奈川県","新潟県","富山県","石川県","福井県","山梨県","長野県","岐阜県","静岡県","愛知県","三重県","滋賀県","京都府","大阪府","兵庫県","奈良県","和歌山県","鳥取県","島根県","岡山県","広島県","山口県","徳島県","香川県","愛媛県","高知県","福岡県","佐賀県","長崎県","熊本県","大分県","宮崎県","鹿児島県","沖縄県"];
const GENDER = ["男性", "女性", "その他", "回答しない"];
const RESIDENCE = ["持ち家", "賃貸", "社宅・寮", "家族所有", "その他"];
const EMPTYPE = ["正社員", "契約社員", "派遣社員", "パート・アルバイト", "自営業", "会社役員", "年金受給", "学生", "無職", "その他"];
const ACCT = ["普通", "当座", "その他"];
const DOCTYPES = ["本人確認書類(表)", "本人確認書類(裏)", "住民票", "収入証明書", "在職証明書", "印鑑証明書", "その他書類"];
const DOCSTATUS = ["未提出", "提出済", "確認中", "承認", "差戻し", "期限切れ"];
const ROLES = [["tenant", "入居者"], ["guarantor", "連帯保証人"], ["emergency_contact", "緊急連絡先"], ["agent", "代理人"]];

const STEPS = [
  { t: "基本情報" }, { t: "連絡先" }, { t: "現住所" }, { t: "役割・区分" }, { t: "勤務先・収入" },
  { t: "緊急連絡先" }, { t: "保証人・保証会社" }, { t: "口座情報" }, { t: "提出書類" }, { t: "確認" },
];
const REQUIRED = ["name", "birth_date", "phone", "email",
  "postal_code", "prefecture", "city", "address_line", "photo"];

const STEP_REQ_I = {
  0: ["name", "birth_date"],
  1: ["phone", "email"],
  2: ["postal_code", "prefecture", "city", "address_line"],
  8: ["photo"],
};

const NATIONALITY = ["日本", "中国", "韓国", "ベトナム", "フィリピン", "ブラジル", "ネパール", "インドネシア", "ペルー", "タイ", "ミャンマー", "台湾", "アメリカ合衆国", "イギリス", "インド", "スリランカ", "バングラデシュ", "その他"];

const WizardCtx = createContext(null);

function F({ k, label, type = "text", opts, req, ph, w }) {
  const { form, setF, missing, t } = useContext(WizardCtx);
  return (
    <div className={w || ""}>
      <label className="block text-sm text-gray-200 mb-1.5 whitespace-nowrap">{t(label)}{(req || REQUIRED.includes(k)) && <span className="text-red-400"> *</span>}</label>
      {type === "select"
        ? <select value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className={IN} data-testid={`i-${k}`}><option value="">{t("選択")}</option>{opts.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select>
        : <input type={type} value={form[k] || ""} placeholder={ph || ""} onChange={(e) => setF(k, e.target.value)} className={IN} data-testid={`i-${k}`} />}
      {missing(k) && <div className="text-xs text-red-400 mt-1">{t("必須項目です")}</div>}
    </div>
  );
}

function ZipRow({ zk, pk, ck, ak, bk, label }) {
  const { form, setF, busy, zip, t } = useContext(WizardCtx);
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="sm:w-52"><label className="block text-sm text-gray-200 mb-1.5 whitespace-nowrap">{t(label + "郵便番号")}{REQUIRED.includes(zk) && <span className="text-red-400"> *</span>}</label>
          <input value={form[zk] || ""} placeholder="000-0000" inputMode="numeric" onChange={(e) => setF(zk, e.target.value)} className={IN} data-testid={`i-${zk}`} /></div>
        <button type="button" onClick={() => zip(zk, pk, ck, ak)} disabled={busy === zk} className="h-[38px] inline-flex items-center justify-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm px-4 rounded-md shrink-0" data-testid={`i-zip-${zk}`}>{busy === zk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}{t("住所検索")}</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><F k={pk} label={`${label}都道府県`} type="select" opts={PREF} /><F k={ck} label={`${label}市区町村`} /><F k={ak} label={`${label}番地`} /></div>
      {bk && <F k={bk} label={`${label}建物名・部屋番号`} />}
    </div>
  );
}

function Repeat({ title, keyName, cols, blank, copyLabel, onCopy }) {
  const { form, arrAdd, arrUpd, arrDel, busy, uploadDoc, t } = useContext(WizardCtx);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">{t(title)}</div>
        <div className="flex gap-2">
          {copyLabel && <button type="button" onClick={onCopy} className="text-xs border border-white/15 rounded px-2 py-1 hover:bg-white/5" data-testid={`i-copy-${keyName}`}>{t(copyLabel)}</button>}
          <button type="button" onClick={() => arrAdd(keyName, { ...blank })} className="text-xs inline-flex items-center gap-1 bg-brand hover:bg-brand-hover text-white rounded px-2 py-1" data-testid={`i-add-${keyName}`}><Plus className="w-3.5 h-3.5" />{t("追加")}</button>
        </div>
      </div>
      {(form[keyName] || []).length === 0 && <div className="text-xs text-gray-500 py-3">{t("未登録")}</div>}
      <div className="space-y-3">
        {(form[keyName] || []).map((row, i) => (
          <div key={i} className="border border-white/10 rounded-md p-3 relative" data-testid={`i-${keyName}-${i}`}>
            <button type="button" onClick={() => arrDel(keyName, i)} className="absolute top-2 right-2 text-gray-500 hover:text-red-400" data-testid={`i-del-${keyName}-${i}`}><Trash2 className="w-4 h-4" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pr-6">
              {cols.map((c) => (
                <div key={c.k}>
                  <label className="block text-xs text-gray-300 mb-1">{t(c.label)}</label>
                  {c.type === "select"
                    ? <select value={row[c.k] || ""} onChange={(e) => arrUpd(keyName, i, c.k, e.target.value)} className={IN}><option value="">{t("選択")}</option>{c.opts.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select>
                    : c.type === "file"
                      ? (row.file ? <div className="flex items-center gap-2 text-xs text-emerald-400"><Check className="w-4 h-4" />{t("アップロード済")}<button type="button" onClick={() => arrUpd(keyName, i, "file", "")} className="text-gray-500"><X className="w-3.5 h-3.5" /></button></div>
                        : <label className="flex items-center gap-1.5 text-xs text-gray-400 border border-dashed border-white/15 rounded px-2 py-2 cursor-pointer hover:bg-white/5">{busy === `doc${i}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{t("選択")}<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(i, e.target.files?.[0])} /></label>)
                      : <input type={c.type || "text"} value={row[c.k] || ""} onChange={(e) => arrUpd(keyName, i, c.k, e.target.value)} className={IN} />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TenantIndividual({ onCorporate }) {
  const base = usePortalBase();
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`${base}/profile`).then(({ data }) => {
      const f = { ...data, account_type: "individual" };
      ["roles", "emergency_contacts", "bank_accounts", "person_documents"].forEach((k) => { if (!Array.isArray(f[k])) f[k] = []; });
      setForm(f);
      if (typeof data.draft_step === "number") setStep(Math.min(9, data.draft_step));
    }).catch(() => setForm(false));
  }, [base]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const missing = (k) => REQUIRED.includes(k) && !form[k];
  const freeNav = base.includes("/tenant");
  const completion = form ? Math.round(REQUIRED.filter((k) => form[k]).length / REQUIRED.length * 100) : 0;

  const arrAdd = (key, obj) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), obj] }));
  const arrUpd = (key, i, k, v) => setForm((f) => { const a = [...f[key]]; a[i] = { ...a[i], [k]: v }; return { ...f, [key]: a }; });
  const arrDel = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const toggleRole = (r) => setForm((f) => { const a = new Set(f.roles || []); a.has(r) ? a.delete(r) : a.add(r); return { ...f, roles: [...a] }; });

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/profile`, { ...form, ...extra, account_type: "individual", draft_step: step, completion });
      setForm((f) => ({ ...f, ...data, account_type: "individual" }));
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("保存に失敗しました")); return false; }
    finally { setSaving(false); }
  };

  const zip = async (zipKey, prefKey, cityKey, addrKey) => {
    const z = (form[zipKey] || "").replace(/[^0-9]/g, "");
    if (z.length !== 7) { toast.error(t("郵便番号は7桁で入力してください")); return; }
    setBusy(zipKey);
    try {
      const r = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${z}`);
      const j = await r.json();
      const res = j.results && j.results[0];
      if (!res) { toast.error(t("住所が見つかりませんでした")); return; }
      setForm((f) => ({ ...f, [prefKey]: res.address1, [cityKey]: res.address2, [addrKey]: (f[addrKey] || "") || res.address3 }));
      toast.success(t("住所を自動入力しました"));
    } catch { toast.error(t("住所取得に失敗しました")); } finally { setBusy(""); }
  };

  const uploadDoc = async (i, file) => {
    if (!file) return;
    setBusy(`doc${i}`);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`${base}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      arrUpd("person_documents", i, "file", data.stored_name);
      arrUpd("person_documents", i, "uploaded_at", new Date().toISOString());
      toast.success(t("アップロードしました"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (form === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (form === false) return <div className="text-gray-500 py-10 text-center">{t("読み込みに失敗しました。")}</div>;

  const card = "bg-[#161616] border border-white/5 rounded-lg p-5 space-y-4";
  const body = (step) => {
    switch (step) {
      case 0: return <div className={card}>
        <F k="name" label="氏名" />
        <F k="name_kana" label="フリガナ" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><F k="birth_date" label="生年月日" type="date" /><F k="gender" label="性別" type="select" opts={GENDER} /><F k="nationality" label="国籍" type="select" opts={NATIONALITY} /></div>
      </div>;
      case 1: return <div className={card}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="phone" label="電話番号" type="tel" /><F k="fax" label="FAX番号" type="tel" /></div>
        <F k="email" label="メールアドレス" type="email" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="line_id" label="LINE ID" /><F k="whatsapp" label="WhatsApp番号" type="tel" /></div>
      </div>;
      case 2: return <div className={card}>
        <ZipRow zk="postal_code" pk="prefecture" ck="city" ak="address_line" bk="building" label="" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="residence_type" label="住居区分" type="select" opts={RESIDENCE} /><F k="years_at_address" label="居住年数" /></div>
      </div>;
      case 3: return <div className={card}>
        <div className="text-sm font-medium mb-1">{t("この個人の役割")}<span className="text-red-400"> *</span></div>
        <div className="text-xs text-gray-500 -mt-2 mb-2">{t("1件のマスター登録で、所有者・入居者・保証人など複数の役割を保持できます。")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ROLES.map(([val, lbl]) => (
            <label key={val} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2.5 cursor-pointer ${(form.roles || []).includes(val) ? "border-brand bg-brand/10 text-white" : "border-white/10 text-gray-300 hover:bg-white/5"}`} data-testid={`i-role-${val}`}>
              <input type="checkbox" checked={(form.roles || []).includes(val)} onChange={() => toggleRole(val)} />{t(lbl)}
            </label>
          ))}
        </div>
      </div>;
      case 4: return <div className={card}>
        <F k="employer" label="勤務先名" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><F k="employer_dept" label="部署" /><F k="employer_title" label="役職" /><F k="employment_type" label="雇用形態" type="select" opts={EMPTYPE} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="employer_phone" label="勤務先電話番号" type="tel" /><F k="years_employed" label="勤続年数" /></div>
        <F k="annual_income" label="年収（円）" type="number" />
      </div>;
      case 5: return <div className={card}><Repeat title="緊急連絡先" keyName="emergency_contacts" blank={{ name: "", relation: "", phone: "", address: "" }} cols={[{ k: "name", label: "氏名" }, { k: "relation", label: "続柄" }, { k: "phone", label: "電話番号" }, { k: "address", label: "住所" }]} /></div>;
      case 6: return <div className={card}>
        <div className="text-sm font-medium mb-1">{t("連帯保証人")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="guarantor" label="連帯保証人 氏名" /><F k="guarantor_relation" label="続柄" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="guarantor_phone" label="電話番号" type="tel" /><F k="guarantor_address" label="住所" /></div>
        <div className="pt-3 border-t border-white/10 space-y-4">
          <div className="text-sm font-medium">{t("保証会社")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="guarantee_company" label="保証会社名" /><F k="guarantee_number" label="保証番号" /></div>
        </div>
      </div>;
      case 7: return <div className={card}><Repeat title="口座情報" keyName="bank_accounts" blank={{ bank_name: "", bank_code: "", branch_name: "", branch_code: "", account_type: "普通", account_number: "", holder: "", holder_kana: "" }} cols={[{ k: "bank_name", label: "金融機関名" }, { k: "bank_code", label: "金融機関コード" }, { k: "branch_name", label: "支店名" }, { k: "branch_code", label: "支店コード" }, { k: "account_type", label: "預金種別", type: "select", opts: ACCT }, { k: "account_number", label: "口座番号" }, { k: "holder", label: "口座名義" }, { k: "holder_kana", label: "口座名義カナ" }]} /></div>;
      case 8: return <div className={card}>
        <ProfilePhoto label="本人写真" value={form.photo} onChange={(v) => setF("photo", v)} testid="i-photo" required />
        <div className="pt-4 border-t border-white/10">
          <div className="text-sm font-medium mb-2">{t("身分証明書")}</div>
          <IdDocument docType={form.id_doc_type} front={form.doc_identity_front} back={form.doc_identity_back} onDocType={(v) => setF("id_doc_type", v)} onFront={(v) => setF("doc_identity_front", v)} onBack={(v) => setF("doc_identity_back", v)} testidPrefix="i-id" />
        </div>
        <div className="pt-4 border-t border-white/10">
          <Repeat title="その他書類" keyName="person_documents" blank={{ doc_type: DOCTYPES[0], name: "", file: "", issue_date: "", expiry: "", status: "未提出" }} cols={[{ k: "doc_type", label: "書類種類", type: "select", opts: DOCTYPES }, { k: "name", label: "書類名" }, { k: "file", label: "ファイル", type: "file" }, { k: "issue_date", label: "発行日", type: "date" }, { k: "expiry", label: "有効期限", type: "date" }, { k: "status", label: "確認状態", type: "select", opts: DOCSTATUS }]} />
        </div>
      </div>;
      case 9: return <div className={card}>
        <div className="text-sm font-medium mb-1">{t("登録担当者")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><F k="registrar_name" label="担当者氏名" /><F k="registrar_phone" label="電話番号" type="tel" /></div>
        <F k="registrar_email" label="メールアドレス" type="email" />
        <div className="pt-3 border-t border-white/10 space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={!!form.confirmed_input} onChange={(e) => setF("confirmed_input", e.target.checked)} data-testid="i-confirmed" />{t("入力内容を確認しました")}</label>
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={!!form.agreed_terms} onChange={(e) => setF("agreed_terms", e.target.checked)} data-testid="i-agreed" />{t("利用規約・プライバシーポリシーに同意します")}</label>
        </div>
      </div>;
      default: return null;
    }
  };

  const stepIcon = (i) => {
    if (i === step) return <Dot className="w-4 h-4 text-brand" />;
    return i < step ? <Check className="w-4 h-4 text-emerald-400" /> : <Circle className="w-3.5 h-3.5 text-gray-600" />;
  };

  const register = async () => {
    if (!form.confirmed_input || !form.agreed_terms) { toast.error(t("確認・同意にチェックしてください")); return; }
    if (!(form.roles || []).length) { toast.error(t("役割を1つ以上選択してください")); return; }
    const miss = REQUIRED.filter((k) => !form[k]);
    if (miss.length) { toast.error(`${t("未入力の必須項目があります")}（${miss.length}${t("件")}）`); return; }
    if (await save({ registered: true })) toast.success(t("個人情報を登録しました"));
  };

  return (
    <WizardCtx.Provider value={{ form, setF, missing, t, busy, zip, arrAdd, arrUpd, arrDel, uploadDoc }}>
    <div data-testid="tenant-individual" className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-light">{t("個人登録")}</h1>
        <button onClick={onCorporate} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="i-to-corporate"><Building2 className="w-3.5 h-3.5" />{t("法人に切替")}</button>
      </div>
      <div className="mb-4"><div className="flex items-center justify-between text-xs text-gray-300 mb-1"><span>{t("入力完了率")}</span><span>{completion}%</span></div><div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${completion}%` }} /></div></div>

      {freeNav ? (
        <div className="space-y-6" data-testid="i-singlepage">
          {STEPS.map((s, i) => (
            <section key={i} data-testid={`i-section-${i}`}>
              <h2 className="text-sm font-medium text-brand mb-2">{i + 1}. {t(s.t)}</h2>
              {body(i)}
            </section>
          ))}
          <div className="sticky bottom-0 z-10 -mx-1 px-1 py-3 bg-[#0f0f0f]/95 backdrop-blur border-t border-white/10 flex flex-wrap items-center gap-2">
            <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-1.5 border border-white/15 text-gray-200 rounded-md px-4 py-2 text-sm hover:bg-white/5" data-testid="i-draft">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t("保存")}</button>
            <button onClick={register} disabled={saving} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-5 py-2 text-sm ml-auto" data-testid="i-register"><Check className="w-4 h-4" />{t("登録する")}</button>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="i-stepper">
          {STEPS.map((s, i) => (
            <button key={i} onClick={() => { if (freeNav || i <= step) setStep(i); else toast.error(t("前のステップを順番に入力してください")); }} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap ${i === step ? "bg-brand/15 text-white" : "text-gray-400 hover:bg-white/5"}`} data-testid={`i-step-${i}`}>
              {stepIcon(i)}<span>{i + 1} {t(s.t)}</span>
            </button>
          ))}
        </nav>

        <div>
          {body(step)}
          <div className="flex flex-wrap items-center gap-2 mt-5">
            <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-1.5 border border-white/15 text-gray-200 rounded-md px-4 py-2 text-sm hover:bg-white/5" data-testid="i-draft">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t("下書き保存")}</button>
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-1 border border-white/15 text-gray-200 rounded-md px-4 py-2 text-sm disabled:opacity-40" data-testid="i-back"><ChevronLeft className="w-4 h-4" />{t("戻る")}</button>
            {step < 9
              ? <button onClick={() => { const m = freeNav ? [] : (STEP_REQ_I[step] || []).filter((k) => !form[k]); if (!freeNav && step === 3 && !((form.roles || []).length)) m.push("roles"); if (m.length) { toast.error(t("このステップの必須項目を入力してください")); return; } save(); setStep((s) => Math.min(9, s + 1)); }} className="inline-flex items-center gap-1 bg-brand hover:bg-brand-hover text-white rounded-md px-4 py-2 text-sm ml-auto" data-testid="i-next">{t("次へ")}<ChevronRight className="w-4 h-4" /></button>
              : <button onClick={register} disabled={saving} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-5 py-2 text-sm ml-auto" data-testid="i-register"><Check className="w-4 h-4" />{t("登録する")}</button>}
          </div>
        </div>
      </div>
      )}
    </div>
    </WizardCtx.Provider>
  );
}
